import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchWorkItemsByProjectId, updateWorkItem } from "@/Redux/Thunks/projectManagementThunk";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import {
  selectIssuesData,
  selectIssuesLoading,
  selectIssuesError,
  updateIssueStatus,
} from "@/Redux/Slices/issuesSlice";
import {
  selectProjectStatusesLoading,
  selectProjectStatusesError,
  selectProjectStatusesResponse,
} from "@/Redux/Slices/projectStatusesSlice";
import { selectKanbanBoard } from "@/Redux/Slices/kanbanColumnsSlice";
import {
  fetchKanbanBoardBootstrap,
  fetchKanbanColumnPage,
} from "@/Redux/Thunks/kanbanColumnsThunk";
import { buildProjectKanbanCategoryColumns } from "@/Pages/Issues/issueMappers";
import { KANBAN_DEFAULT_LIMIT } from "@/constants";
import { useDebounce } from "@/hooks/useDebounce";
import KanbanBoard from "./KanbanBoard";
import ScrumBoard from "./ScrumBoard";
import IssueList from "@/Pages/Issues/IssueList";
import { ContentLoader } from "@/Utility/Loader";
import Typography from "@/Utility/Typography";
import { getBoardSettingsStorageKey } from "./boards.mapper";

const ProjectsBoards = ({ onOpenWorkItemDetail, boardsListRefetchContextRef }) => {
  const dispatch = useDispatch();
  const { projectSlug } = useParams();

  // Flat-list state — used by table and scrum views only.
  const issuesData = useSelector(selectIssuesData);
  const loading = useSelector(selectIssuesLoading);
  const error = useSelector(selectIssuesError);

  const statusesLoading = useSelector((state) =>
    projectSlug ? selectProjectStatusesLoading(state, projectSlug) : false
  );
  const statusesError = useSelector((state) =>
    projectSlug ? selectProjectStatusesError(state, projectSlug) : null
  );
  const statusesResponse = useSelector((state) =>
    projectSlug ? selectProjectStatusesResponse(state, projectSlug) : null
  );

  // Per-column kanban state for the project board.
  const projectKanbanBoardState = useSelector((state) =>
    projectSlug ? selectKanbanBoard(state, projectSlug) : null
  );

  const storageKey = getBoardSettingsStorageKey(projectSlug);

  const [settings, setSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem(storageKey)) || {
        boardType: "kanban",
        viewMode: "board",
      }
    );
  });
  const [statusPatchPending, setStatusPatchPending] = useState(false);
  const [projectKanbanAssignees, setProjectKanbanAssignees] = useState([]);
  const [projectKanbanSearchTerm, setProjectKanbanSearchTerm] = useState("");
  const debouncedProjectKanbanSearchTerm = useDebounce(projectKanbanSearchTerm, 500);

  useEffect(() => {
    setProjectKanbanAssignees([]);
    setProjectKanbanSearchTerm("");
  }, [projectSlug]);

  const isProjectKanbanBoard = settings.viewMode === "board" && settings.boardType === "kanban";

  // Fetch project statuses and, for non-kanban views, the flat work-item list.
  useEffect(() => {
    if (!projectSlug) return;
    dispatch(fetchProjectStatuses(projectSlug));
    if (!isProjectKanbanBoard) {
      dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug }));
    }
  }, [dispatch, projectSlug, isProjectKanbanBoard]);

  // Kanban-only: fetch first 50 rows for all visible project columns in one request.
  useEffect(() => {
    if (!projectSlug || !isProjectKanbanBoard) return;
    if (statusesLoading || !statusesResponse) return;

    const columns = buildProjectKanbanCategoryColumns(statusesResponse);
    if (!columns.length) return;

    const assigneeId = projectKanbanAssignees.length > 0 ? projectKanbanAssignees : undefined;
    dispatch(
      fetchKanbanBoardBootstrap({
        boardKey: projectSlug,
        projectId: projectSlug,
        assigneeId,
        limit: KANBAN_DEFAULT_LIMIT,
        columns,
        search: debouncedProjectKanbanSearchTerm,
      })
    );
  }, [
    dispatch,
    projectSlug,
    isProjectKanbanBoard,
    projectKanbanAssignees,
    statusesResponse,
    statusesLoading,
    debouncedProjectKanbanSearchTerm,
  ]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(settings));
  }, [settings, storageKey]);

  useEffect(() => {
    const ref = boardsListRefetchContextRef;
    if (!ref || typeof ref !== "object" || !("current" in ref)) return;
    const mode =
      settings.viewMode === "table"
        ? "table"
        : settings.boardType === "kanban"
          ? "kanban"
          : "scrum";
    ref.current = {
      mode,
      kanbanAssigneeIds: mode === "kanban" ? [...projectKanbanAssignees] : [],
    };
  }, [boardsListRefetchContextRef, settings.viewMode, settings.boardType, projectKanbanAssignees]);

  /** Reset all kanban columns and re-fetch first page. Used after create, save, or drag-drop. */
  const refetchProjectKanbanIssues = useCallback(() => {
    if (!projectSlug || !statusesResponse) return;
    const columns = buildProjectKanbanCategoryColumns(statusesResponse);
    if (!columns.length) return;

    const assigneeId = projectKanbanAssignees.length > 0 ? projectKanbanAssignees : undefined;
    return dispatch(
      fetchKanbanBoardBootstrap({
        boardKey: projectSlug,
        projectId: projectSlug,
        assigneeId,
        limit: KANBAN_DEFAULT_LIMIT,
        columns,
        search: debouncedProjectKanbanSearchTerm,
      })
    );
  }, [
    dispatch,
    projectSlug,
    projectKanbanAssignees,
    statusesResponse,
    debouncedProjectKanbanSearchTerm,
  ]);

  const handleScrumStatusChange = (issueId, newStatus, sprintId) => {
    dispatch(updateIssueStatus({ issueId, newStatus, sprintId }));
  };

  const handleKanbanStatusChange = async (issueId, payload) => {
    const statusId = payload != null && typeof payload === "object" ? payload.statusId : payload;
    if (!projectSlug || !issueId || statusId == null || statusId === "") return;

    // Find version from kanban column state (issues=[] in lazy mode).
    const allItems = projectKanbanBoardState
      ? Object.values(projectKanbanBoardState).flatMap((col) => col.items ?? [])
      : [];
    const issue = allItems.find((i) => i.id === issueId);
    const version = issue?.version ?? 1;

    setStatusPatchPending(true);
    try {
      await dispatch(
        updateWorkItem({
          projectId: projectSlug,
          workItemId: issueId,
          body: { version, statusId },
        })
      ).unwrap();
      await refetchProjectKanbanIssues();
    } catch {
      /* toast from ApiBase */
    } finally {
      setStatusPatchPending(false);
    }
  };

  /** Dispatch load-more for a single kanban column. */
  const handleProjectLoadMore = useCallback(
    (columnKey) => {
      const colState = projectKanbanBoardState?.[columnKey];
      if (!colState || colState.loadingMore || !colState.hasMore) return;

      const columns = buildProjectKanbanCategoryColumns(statusesResponse);
      const col = columns.find((c) => c.key === columnKey);
      if (!col?.category) return;

      const assigneeId = projectKanbanAssignees.length > 0 ? projectKanbanAssignees : undefined;
      dispatch(
        fetchKanbanColumnPage({
          boardKey: projectSlug,
          columnKey,
          isLoadMore: true,
          projectId: projectSlug,
          statusCategories: col.category,
          assigneeId,
          page: (colState.page ?? 1) + 1,
          limit: KANBAN_DEFAULT_LIMIT,
          search: debouncedProjectKanbanSearchTerm,
        })
      );
    },
    [
      dispatch,
      projectSlug,
      projectKanbanBoardState,
      projectKanbanAssignees,
      statusesResponse,
      debouncedProjectKanbanSearchTerm,
    ]
  );

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {error}
          </Typography>
        </div>
      </div>
    );
  }

  if (projectSlug && isProjectKanbanBoard && statusesError && !statusesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {statusesError}
          </Typography>
        </div>
      </div>
    );
  }

  // For table/scrum views, show a loading placeholder until issuesData arrives.
  const showNonKanbanPlaceholder = !isProjectKanbanBoard && (loading || (!issuesData && !error));

  const projectIssues = issuesData?.list ?? [];

  return (
    <div className="mt-4">
      <ContentLoader loading={showNonKanbanPlaceholder} className="min-h-[calc(100vh-12rem)]">
        {showNonKanbanPlaceholder ? (
          <div className="min-h-[50vh] w-full" aria-hidden />
        ) : settings.viewMode === "table" ? (
          <IssueList
            issuesData={{ list: projectIssues }}
            projectSlug={projectSlug}
            onIssueCreated={() => {
              if (projectSlug) {
                dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug }));
              }
            }}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
          />
        ) : settings.boardType === "kanban" ? (
          <KanbanBoard
            issues={[]}
            onStatusChange={handleKanbanStatusChange}
            projectSlug={projectSlug}
            statusPatchPending={statusPatchPending}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
            assigneeFilterIds={projectKanbanAssignees}
            onAssigneeFilterIdsChange={setProjectKanbanAssignees}
            onRefetchProjectKanbanIssues={refetchProjectKanbanIssues}
            forceListLoading={false}
            columnData={projectKanbanBoardState ?? undefined}
            onLoadMore={handleProjectLoadMore}
            searchTerm={projectKanbanSearchTerm}
            onSearchTermChange={setProjectKanbanSearchTerm}
          />
        ) : (
          <ScrumBoard
            issues={projectIssues}
            onStatusChange={handleScrumStatusChange}
            projectSlug={projectSlug}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
          />
        )}
      </ContentLoader>
    </div>
  );
};

export default ProjectsBoards;
